import { getFormTabErrorCounts } from '../../../../../lib/formTabErrors';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm, useFormState, useWatch } from 'react-hook-form';
import { Braces, ListChecks, UsersRound } from 'lucide-react';
import {
  DashboardPageFormActions,
  DashboardPageFormTab,
  DashboardPageFormTabList,
  DashboardPageFormTabPanel,
} from '@shigo-ui/dashboard-ui';
import { Button, Form } from '@shigo-ui/ui';

import { showRequestError } from '../../../../../lib/requestErrorToast';
import { useReturnNavigation } from '../../../../../lib/returnNavigation';
import { EditorSectionTabs } from '../../../../../components/forms/EditorSectionTabs';
import { useEditorSectionControls } from '../../../../../components/forms/useEditorSection';
import { dashboardT } from '../../../../../lib/messages';
import { downloadBlob } from '../../../../../lib/downloadBlob';
import { NetworkAvailableFeature } from '../../../../auth/networkFeatures';
import { useNetworkFeatures } from '../../../../auth/useNetworkFeatures';
import type { TargetingClient } from '../../../api';
import { standaloneTargetingConfig } from '../../../config';
import { TargetingForm } from '../../../components/TargetingForm/TargetingForm';
import { CustomTargetingForm } from '../../../components/CustomTargetingForm';
import { LiverampTargetingForm } from '../../../components/LiverampTargetingForm';
import type { TargetingBuilderClient } from '../../api';
import { targetingBuilderFormSchema } from '../../targetingBuilderFormSchema';
import { TargetingBuilderEditorTab, type TargetingBuilderFormValue } from '../../model';
import { TargetingBuilderIdentityFields } from './TargetingBuilderIdentityFields';

export function TargetingBuilderEditorForm({
  initialForm,
  targetingId = '',
  saving,
  targetingClient,
  builderClient,
  onSubmit,
}: {
  initialForm: TargetingBuilderFormValue;
  targetingId?: string | undefined;
  saving: boolean;
  targetingClient: TargetingClient;
  builderClient: TargetingBuilderClient;
  onSubmit: (form: TargetingBuilderFormValue) => void;
}) {
  'use no memo';

  const { returnTo } = useReturnNavigation({ to: '/targeting' });
  const { isFeatureAllowed } = useNetworkFeatures();
  const liveRampAllowed = isFeatureAllowed(NetworkAvailableFeature.LiveRamp);
  const methods = useForm<TargetingBuilderFormValue>({
    defaultValues: initialForm,
    resolver: zodResolver(targetingBuilderFormSchema),
  });
  const { errors, submitCount } = useFormState({ control: methods.control });
  const [editorSection, setActiveTab] = useEditorSectionControls(
    TargetingBuilderEditorTab.Targeting,
    Object.values(TargetingBuilderEditorTab).filter(
      (tab) => liveRampAllowed || tab !== TargetingBuilderEditorTab.Liveramp,
    ),
  );
  const targetingErrors = errors.targeting as Record<string, { message?: string }> | undefined;
  const bundlesCount = useWatch({ control: methods.control, name: 'bundlesCount' });
  const ipsCount = useWatch({ control: methods.control, name: 'ipsCount' });
  const pendingBundleCsv = useWatch({ control: methods.control, name: 'pendingBundleCsv' });
  const pendingIpCsv = useWatch({ control: methods.control, name: 'pendingIpCsv' });

  const tabErrors = getFormTabErrorCounts(
    errors,
    {
      [TargetingBuilderEditorTab.Custom]: ['customTargeting'],
      [TargetingBuilderEditorTab.Liveramp]: ['liverampTargeting'],
    },
    TargetingBuilderEditorTab.Targeting,
  );

  return (
    <Form {...methods}>
      <form
        noValidate
        onSubmit={(event) => {
          void methods.handleSubmit(
            (form) => {
              if (!saving) onSubmit(form);
            },
            (errors) => {
              if (errors.customTargeting) setActiveTab(TargetingBuilderEditorTab.Custom);
              else if (liveRampAllowed && errors.liverampTargeting) {
                setActiveTab(TargetingBuilderEditorTab.Liveramp);
              } else setActiveTab(TargetingBuilderEditorTab.Targeting);
            },
          )(event);
        }}
      >
        <EditorSectionTabs controls={editorSection}>
          <DashboardPageFormTabList>
            <DashboardPageFormTab
              value={TargetingBuilderEditorTab.Targeting}
              errorCount={tabErrors[TargetingBuilderEditorTab.Targeting]}
              errorLabel={(count) => dashboardT('common.validationErrors', { count })}
              icon={<ListChecks className="h-4 w-4" />}
            >
              {dashboardT('targetingBuilder.targetingTab')}
            </DashboardPageFormTab>
            <DashboardPageFormTab
              value={TargetingBuilderEditorTab.Custom}
              errorCount={tabErrors[TargetingBuilderEditorTab.Custom]}
              errorLabel={(count) => dashboardT('common.validationErrors', { count })}
              icon={<Braces className="h-4 w-4" />}
            >
              {dashboardT('targetingBuilder.customTab')}
            </DashboardPageFormTab>
            {liveRampAllowed ? (
              <DashboardPageFormTab
                value={TargetingBuilderEditorTab.Liveramp}
                errorCount={tabErrors[TargetingBuilderEditorTab.Liveramp]}
                errorLabel={(count) => dashboardT('common.validationErrors', { count })}
                icon={<UsersRound className="h-4 w-4" />}
              >
                {dashboardT('targetingBuilder.liverampTab')}
              </DashboardPageFormTab>
            ) : null}
          </DashboardPageFormTabList>
          <DashboardPageFormTabPanel value={TargetingBuilderEditorTab.Targeting}>
            <Controller
              control={methods.control}
              name="targeting"
              render={({ field }) => (
                <TargetingForm
                  config={standaloneTargetingConfig}
                  client={targetingClient}
                  liveRampAllowed={liveRampAllowed}
                  csvControls={{
                    bundles: {
                      targetingId,
                      count: bundlesCount,
                      file: pendingBundleCsv,
                      onFileChange: (file, rowCount) => {
                        methods.setValue('pendingBundleCsv', file);
                        methods.setValue('bundlesCount', rowCount);
                      },
                      onExport: () => {
                        if (!targetingId) return;
                        void targetingClient
                          .exportBundlesCsv(targetingId)
                          .then((blob) => {
                            downloadBlob(blob, 'targeting-bundles.csv');
                          })
                          .catch((error: unknown) => {
                            showRequestError(
                              error,
                              dashboardT('integrations.targetingCsvExportError'),
                            );
                          });
                      },
                    },
                    ips: {
                      targetingId,
                      count: ipsCount,
                      file: pendingIpCsv,
                      onFileChange: (file, rowCount) => {
                        methods.setValue('pendingIpCsv', file);
                        methods.setValue('ipsCount', rowCount);
                      },
                      onExport: () => {
                        if (!targetingId) return;
                        void targetingClient
                          .exportIpsCsv(targetingId)
                          .then((blob) => {
                            downloadBlob(blob, 'targeting-ips.csv');
                          })
                          .catch((error: unknown) => {
                            showRequestError(
                              error,
                              dashboardT('integrations.targetingCsvExportError'),
                            );
                          });
                      },
                    },
                  }}
                  errors={Object.fromEntries(
                    Object.entries(targetingErrors ?? {}).map(([key, error]) => [
                      key,
                      error.message ?? '',
                    ]),
                  )}
                  leadingFields={<TargetingBuilderIdentityFields />}
                  submitted={submitCount > 0}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </DashboardPageFormTabPanel>
          <DashboardPageFormTabPanel value={TargetingBuilderEditorTab.Custom}>
            <CustomTargetingForm />
          </DashboardPageFormTabPanel>
          {liveRampAllowed ? (
            <DashboardPageFormTabPanel value={TargetingBuilderEditorTab.Liveramp}>
              <LiverampTargetingForm client={builderClient} />
            </DashboardPageFormTabPanel>
          ) : null}
        </EditorSectionTabs>
        <DashboardPageFormActions>
          <Button
            type="button"
            variant="outline"
            className="rounded-full px-5"
            disabled={saving}
            onClick={() => {
              void returnTo();
            }}
          >
            {dashboardT('targetingBuilder.cancel')}
          </Button>
          <Button type="submit" className="rounded-full px-5" disabled={saving}>
            {saving ? dashboardT('targetingBuilder.saving') : dashboardT('targetingBuilder.save')}
          </Button>
        </DashboardPageFormActions>
      </form>
    </Form>
  );
}
